<template>
<div>
  <Navbar />
    <section class="hero fixed-nav profile plan-workspace">
      <div class="hero-body">
        <div class="workspace-heading">
          <h1>Vertretungsplan</h1>
          <span class="publication-status" :class="{ published: latest_plan.published }">
            {{ latest_plan.published ? 'Veröffentlicht' : 'Entwurf' }}
          </span>
        </div>
        <div class="columns">
          <div class="column">
            <div class="plan-date-controls">
              <span class="plan-date-label">für den Tag:</span>
              <div class="inline">
                <button class="date-button" aria-label="Vorheriger Tag" @click="go_back">
                  <font-awesome-icon :icon="['fa', 'angle-left']"/>
                </button>
                <b-datepicker
                    style="margin: 0 .2rem"
                    v-model="plan_for_date"
                    @input="get_plan"
                    :key="plan_for_date.toString()"
                    :data="plan_for_date"
                    placeholder="Hier klicken..."
                    aria-label="Datum des Vertretungsplans"
                    icon="calendar-today"
                    :unselectable-days-of-week="[0, 6]"
                    close-on-click>
                </b-datepicker>
                <button :icon="['fa', 'angle-right']" @click="go_next"
                  class="date-button" aria-label="Nächster Tag">
                  <font-awesome-icon :icon="['fa', 'angle-right']"/>
                </button>
              </div>
              <b-switch :value="latest_plan.published === true"
                :disabled="loading || publishing || !latest_plan.id"
                @input="set_published">Veröffentlicht</b-switch>
            </div>
          </div>
        </div>
        <div class="plan-toolbar" aria-label="Planaktionen">
          <b-button :disabled="is_old || isLoading" :loading="isLoading"
            @click="make_plan">Plan aktualisieren</b-button>
          <b-button :disabled="is_old" @click="edit = !edit">
            {{ edit ? 'Bearbeiten beenden' : 'Bearbeiten' }}
          </b-button>
          <b-button v-if="edit" class="primary-action" :loading="saving"
            @click="save_plan">Speichern</b-button>
          <b-dropdown position="is-bottom-left" aria-role="list">
            <template #trigger><b-button>Weitere Aktionen ▾</b-button></template>
            <b-dropdown-item :disabled="is_old" @click="add_sub">
              Vertretung eintragen
            </b-dropdown-item>
            <b-dropdown-item :disabled="saving" @click="prepare_send_plan">
              Plan versenden</b-dropdown-item>
            <b-dropdown-item @click="show_reg_plan">Stundenplan ansehen</b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="absence-summary">
          <div><span>Krank</span><p v-html="latest_plan.ill || 'Keine Meldungen'"></p></div>
          <div><span>Beurlaubt / Dienstlich</span>
            <p v-html="latest_plan.excused || 'Keine Meldungen'"></p></div>
          <div><span>Fahrt / Praktikum</span>
            <p v-html="latest_plan.trip || 'Keine Meldungen'"></p></div>
        </div>
        <div class="plan-table-heading">
          <h2>Vertretungen <span>{{ filtered_subs.length }}
            von {{ (latest_plan.subs || []).length }}</span></h2>
          <div class="status-legend">
            <span class="status-chip status-open">Offen ({{ status_counts.alert }})</span>
            <span class="status-chip status-combined">
              Zusammengelegt ({{ status_counts['combined-class'] }})</span>
            <span class="status-chip status-agreement">
              Absprache ({{ status_counts.agree_grey }})</span>
          </div>
        </div>
        <div class="plan-filters">
          <b-field label="Klasse" label-for="plan-class-filter">
            <b-input id="plan-class-filter" v-model="classFilter"
              type="search" placeholder="Klasse filtern …" />
          </b-field>
          <b-field label="Stunde" label-for="plan-hour-filter">
            <b-input id="plan-hour-filter" v-model="hourFilter"
              type="search" placeholder="Stunde filtern …" />
          </b-field>
          <b-field label="Fehlende(r)" label-for="plan-missing-filter">
            <b-input id="plan-missing-filter" v-model="missingFilter"
              type="search" placeholder="Name filtern …" />
          </b-field>
          <b-checkbox v-model="openOnly" size="is-small" class="open-only-filter">
            offene anzeigen</b-checkbox>
          <b-button v-if="classFilter || hourFilter || missingFilter || openOnly"
            @click="classFilter = ''; hourFilter = ''; missingFilter = ''; openOnly = false">
            Filter zurücksetzen</b-button>
        </div>
        <b-table class="plan-table app-table" v-if="!loading"
          @dblclick.native="!is_old && (edit = true)"
          :data="filtered_subs"
          :row-class="(row, index) => rowcolor(row)"
          :bordered="false"
          :striped="false"
          :narrowed="false"
          :hoverable="true"
          :loading="false"
          :focusable="false"
          :mobile-cards="true"
          :paginated="false">
          <b-table-column field="class" label="Klasse" sortable v-slot="props">
            <strong>{{ props.row.class }}</strong>
          </b-table-column>
          <b-table-column field="hour" label="Stunde" sortable v-slot="props">
            {{ props.row.hour }}
          </b-table-column>
          <b-table-column field="subject" label="Fach" sortable v-slot="props">
            {{ props.row.subject }}
          </b-table-column>
          <b-table-column field="missing" label="Fehlende(r)" sortable v-slot="props">
            <b-tooltip v-if="props.row.missing.subjects.length > 0"
              :label="props.row.missing.subjects.map(s => s.name).join(', ').toString()"
              position="is-right"> {{props.row.missing.lastname}}
            </b-tooltip>
            <span v-else>{{ props.row.missing.lastname }}</span>
          </b-table-column>

          <b-table-column field="substitute" label="Vertretung" v-slot="props">
            <b-dropdown v-if="edit" class="substitute-dropdown" append-to-body
            position="is-bottom-left"
            aria-role="list" @input="substchange(props.row, $event)"
            @active-change="teacherQuery = ''"
            :value="props.row.substitute" :scrollable="true" :max-height="400">
              <template #trigger="{ active }">
                <b-button :label="substituteLabel(props.row)"
                  :icon-right="active ? 'menu-up' : 'menu-down'" />
              </template>
              <div class="dropdown-search" @click.stop @keydown.stop>
                <b-input v-model="teacherQuery" placeholder="Lehrkraft suchen …"
                  aria-label="Lehrkraft suchen" size="is-small" />
              </div>
              <p class="dropdown-heading">Bereitschaft</p>
              <b-dropdown-item v-for="alternative in filterTeachers(standbyTeachers(props.row))"
              :key="'standby_'+alternative.id" aria-role="listitem" :value="alternative">
                {{ alternative.firstname + ' ' + alternative.lastname + ' ('
                  + (alternative.standby_hours == null ? '-' : alternative.standby_hours) + ')'}}
                · Woche: {{ standbyHoursWeek(alternative) }}
              </b-dropdown-item>
              <hr>
              <p class="dropdown-heading">Verfügbar durch Klassenabwesenheit</p>
              <b-dropdown-item v-for="free_teacher in filterTeachers(props.row.free_teachers)"
              :key="'free_'+free_teacher.id"
              aria-role="listitem" :value="{ classAbsence: true, teacher: free_teacher }">
                {{ free_teacher.firstname }} {{ free_teacher.lastname }}
                <span v-if="free_teacher.absent_classes && free_teacher.absent_classes.length">
                  ({{ free_teacher.absent_classes.join(', ') }})
                </span>
                · Woche: {{ standbyHoursWeek(free_teacher) }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!props.row.free_teachers || !props.row.free_teachers.length"
                disabled>Keine Lehrkräfte</b-dropdown-item>
              <hr>
              <b-dropdown-item aria-role="listitem"
              :value="{lastname: 'Nur Bemerkung'}">
                <b>Nur Bemerkung</b>
              </b-dropdown-item>
              <template
                v-if="String(props.row.subject || '').trim().toLowerCase() !== 'sprachstreifen'">
                <b-dropdown-item v-for="teacher in filterTeachers(props.row.parallel_teachers)"
                  :key="'parallel_'+teacher.id" aria-role="listitem"
                  :value="{ combine: true, teacher }">
                  <b>Zusammenlegen</b>
                  <span>
                    – {{ teacher.firstname }} {{ teacher.lastname }}
                    · Woche: {{ standbyHoursWeek(teacher) }}
                  </span>
                </b-dropdown-item>
              </template>
              <b-dropdown-item aria-role="listitem" :value="{lastname: 'entfällt'}">
                <b>entfällt</b>
              </b-dropdown-item>
              <hr>
              <p class="dropdown-heading">Alle Lehrkräfte</p>
              <b-dropdown-item v-for="teacher in filterTeachers(available_teachers)"
              :key="'available_'+teacher.lastname"
              aria-role="listitem" :value="teacher">
                {{ teacher.fullName }}
                · Woche: {{ standbyHoursWeek(teacher) }}
              </b-dropdown-item>
            </b-dropdown>
            <span v-else>
              <b-tooltip v-if="props.row.substitute.subjects"
              :label="props.row.substitute.subjects.map(s => s.name).join(', ').toString()"
              position="is-right">
                {{ substituteLabel(props.row) }}
              </b-tooltip>
              <span v-else>{{ substituteLabel(props.row) }}</span>
            </span>
            <b-tooltip v-if="substituteWarning(props.row)" :label="substituteWarning(props.row)"
              append-to-body multilined position="is-top" type="is-warning">
              <span class="substitute-warning" tabindex="0" role="img"
                :aria-label="substituteWarning(props.row)" :title="substituteWarning(props.row)">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
              </span>
            </b-tooltip>
            <span v-if="rowcolor(props.row)" class="row-status">
              {{ rowcolor(props.row) === 'alert' ? 'Offen'
                : rowcolor(props.row) === 'combined-class' ? 'Zusammengelegt'
                  : props.row.noprint ? 'Ausgeblendet' : 'Absprache' }}
            </span>
          </b-table-column>
          <b-table-column field="comment" label="Bemerkung" v-slot="props">
            <b-input v-if="edit" v-model="props.row.comment" type="textarea"
              v-auto-height class="comment-input" rows="1"></b-input>
            <p v-else>{{ props.row.comment }}</p>
          </b-table-column>
          <b-table-column field="noprint" label="Ausblenden" v-slot="props" icon="">
            <b-checkbox v-model="props.row.noprint" aria-label="Eintrag ausblenden">
            </b-checkbox>
          </b-table-column>
          <b-table-column field="fix" label="Fixiert">
            <template #header>
              <b-checkbox :value="all_fixed" :indeterminate="some_fixed && !all_fixed"
                :disabled="loading || !(latest_plan.subs || []).length"
                aria-label="Alle Einträge fixieren oder freigeben" @input="set_all_fixed">
                Fixiert
              </b-checkbox>
            </template>
            <template #default="props">
              <b-checkbox v-model="props.row.fix" aria-label="Eintrag fixieren">
              </b-checkbox>
            </template>
          </b-table-column>
          <template #empty>
            <p class="empty-plan">{{ (latest_plan.subs || []).length
              ? 'Keine Vertretungen für diese Filter gefunden.'
              : 'Für diesen Tag liegen keine Vertretungen vor.' }}</p>
          </template>
        </b-table>
      </div>
    </section>

    <!-- PANEL Vertretung Planen -->
    <div class="modal is-active" v-if="edit_modal">
      <div class="modal-background" @click="reset_selected"></div>
      <div class="modal-card">
        <header class="modal-card-head has-text-centered">
          <p class="title card-title">
            Vertretung planen für den {{ dateFormat(latest_plan.daydate) }}
          </p>
        </header>
        <section class="modal-card-body">
          <div class="mt-5">
            <div class="columns">
              <b-field label="Klasse" class="column">
                <b-input value="" v-model="entry.class"></b-input>
              </b-field>
              <b-field label="Stunde" class="column">
                <b-input value="" v-model="entry.hour"></b-input>
              </b-field>
              <b-field label="Fach" class="column">
                <b-input v-model="entry.subject" value=""></b-input>
              </b-field>
            </div>
            <b-field label="Fehlend">
              <b-autocomplete :key="this.hotloading"
                v-model="name"
                :placeholder="$t('teacher-management.search-placeholder')"
                :keep-first="true"
                :open-on-focus="true"
                :data="search_teachers"
                @select="select_missing($event)"
                field="fullName"
                :empty="[]"
                :clearable="true"
                @input="search(name)">
                <template #empty>Keine Ergebnisse</template>
              </b-autocomplete>
            </b-field>
            <b-field label="Vertretung">
              <b-autocomplete :key="this.hotloading"
                v-model="name_sub"
                :placeholder="$t('teacher-management.search-placeholder')"
                :keep-first="true"
                :open-on-focus="true"
                :data="search_substitutes"
                @select="select_sub($event)"
                field="fullName"
                :empty="[]"
                :clearable="true"
                @input="search_sub(name)">
                <template #empty>Keine Ergebnisse</template>
              </b-autocomplete>
            </b-field>
            <b-field label="Bemerkung">
              <b-input v-model="entry.comment" value="" maxlength="150" type="textarea"></b-input>
            </b-field>
          </div>
        </section>
        <footer class="modal-card-foot">
          <Button :text="$t('common.cancel')" :type="'zoom'"
          @click="reset_selected" />
          <Button :text="$t('common.save')" :type="'zoom'"
          @click="save_sub" />
        </footer>
      </div>
    </div>

    <!-- PANEL Stundenplan -->
    <div class="modal is-active" v-if="plan_modal">
      <div class="modal-background" @click="reset_selected"></div>
      <div class="modal-card">
        <header class="modal-card-head has-text-centered">
          <p class="title card-title">Stundenplan für den {{ latest_plan.daydate }}</p>
        </header>
        <section class="modal-card-body">
          <div class="mt-5">

          </div>
        </section>
        <footer class="modal-card-foot">
          <Button :text="$t('common.cancel')" :type="'zoom'"
          @click="close_plan_modal" />
        </footer>
      </div>
    </div>

    <!-- PANEL Versendung -->
    <div v-if="send_modal" class="modal is-active">
      <div class="modal-background" @click="toggle_send_modal"></div>
      <div class="modal-card">
        <header class="modal-card-head has-text-centered">
          <p class="title card-title">Versand bestätigen</p>
        </header>
        <section class="modal-card-body">
          <b-field label="E-Mail Text" horizontal expanded>
            <b-input maxlength="200" custom-class="mail-input"
            type="textarea" v-model="email_text"></b-input>
          </b-field>
          <div class="block">
            <b-radio v-model="recipient" name="name" native-value="self">
                Zu mir selbst
            </b-radio>
            <b-radio v-model="recipient" name="name" native-value="distributer">
                E-Mail Verteiler
            </b-radio>
          </div>
        </section>
        <footer class="modal-card-foot">
          <Button :text="$t('common.cancel')" :type="'zoom'"
          @click="toggle_send_modal" />
          <Button :text="'Versenden'" :type="'zoom'"
          @click="send_plan" />
        </footer>
      </div>
    </div>

    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="false"
    ></b-loading>

  <Footer />
</div>
</template>

<script>
/* eslint-disable dot-notation */
import moment from 'moment';
import teacherAvailable from '../utils/teacherAvailability';
import substituteConflicts from '../utils/substituteConflicts';
import formatting from '../mixins/formatting';
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';
import Button from '../components/Common/Button.vue';

const commentObservers = new WeakMap();
const resizeComment = (element) => {
  const textarea = element.querySelector('textarea');
  if (!textarea) return;
  textarea.style.height = 'auto';
  const styles = window.getComputedStyle(textarea);
  const border = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
  textarea.style.height = `${textarea.scrollHeight + border}px`;
};

export default {
  directives: {
    autoHeight: {
      inserted(element) {
        resizeComment(element);
        if (typeof ResizeObserver === 'undefined') return;
        let previousWidth = element.clientWidth;
        const observer = new ResizeObserver(() => {
          if (element.clientWidth !== previousWidth) {
            previousWidth = element.clientWidth;
            resizeComment(element);
          }
        });
        observer.observe(element);
        commentObservers.set(element, observer);
      },
      componentUpdated: resizeComment,
      unbind(element) {
        const observer = commentObservers.get(element);
        if (observer) observer.disconnect();
        commentObservers.delete(element);
      },
    },
  },
  components: {
    Navbar,
    Footer,
    Button,
  },
  mixins: [formatting],
  data() {
    return {
      teacherQuery: '',
      classFilter: '',
      hourFilter: '',
      missingFilter: '',
      openOnly: false,
      publishing: false,
      plan_for_date: new Date(),
      table_selected: {},
      edit: false,
      edit_modal: false,
      plan_modal: false,
      send_modal: false,
      saving: false,
      recipient: 'self',
      entry: {
        comment: '',
        color: 'black',
        class: '',
        hour: '',
      },
      hotloading: false,
      name: '',
      name_sub: '',
      isLoading: false,
      email_text: 'Liebes Kollegium,\n\nanbei der Plan für den morgigen Tag.\n\nAlles Gute,\n',
    };
  },

  created() {
    // this.$store.dispatch('Plans/load_latest_plan');
    this.$store.dispatch('Account/load');
    this.$store.dispatch('Teachers/load');
    this.plan_for_date.setDate(this.plan_for_date.getDate() + 1);
    this.get_plan();
    this.email_text += this.user.fullName;
  },

  computed: {
    teacherRecords() {
      return this.$store.getters['Teachers/get_teacher_records'] || [];
    },
    plan() {
      return this.$store.getters['Plans/get_latest_plan'];
    },
    current_weekday() {
      return this.$store.getters['Plans/get_weekday_from_plan'];
    },
    client() {
      return this.$store.getters['Clients/login_client'];
    },
    user() {
      return this.$store.getters['User/user'];
    },
    loading() {
      return this.$store.getters['Plans/loading'];
    },
    latest_plan() {
      return this.$store.getters['Plans/get_latest_plan'];
    },
    filtered_subs() {
      const classQuery = this.classFilter.trim().toLocaleLowerCase('de');
      const hourQuery = this.hourFilter.trim().toLocaleLowerCase('de');
      const missingQuery = this.missingFilter.trim().toLocaleLowerCase('de');
      return (this.latest_plan.subs || []).filter((row) => {
        const teacher = row.missing || {};
        const name = [teacher.firstname, teacher.lastname].filter(Boolean).join(' ');
        return String(row.class || '').toLocaleLowerCase('de').includes(classQuery)
          && [row.hour, ...(row.hours || [])].some((hour) => String(hour || '').toLocaleLowerCase('de').includes(hourQuery))
          && name.toLocaleLowerCase('de').includes(missingQuery)
          && (!this.openOnly || this.rowcolor(row) === 'alert');
      });
    },
    status_counts() {
      const counts = { alert: 0, 'combined-class': 0, agree_grey: 0 };
      this.filtered_subs.forEach((row) => {
        const status = this.rowcolor(row);
        if (Object.prototype.hasOwnProperty.call(counts, status)) counts[status] += 1;
      });
      return counts;
    },
    all_fixed() {
      const rows = this.latest_plan.subs || [];
      return rows.length > 0 && rows.every((row) => row.fix === true);
    },
    some_fixed() {
      return (this.latest_plan.subs || []).some((row) => row.fix === true);
    },
    available_teachers() {
      return this.teacherRecords.filter(this.teacherAvailable);
    },
    get_completete_search_list() {
      return this.$store.getters['Teachers/get_completete_search_list'];
    },
    search_teachers() {
      if (this.$store.getters['Teachers/get_search_list'].length === 0) {
        return this.teachers;
      }
      return this.$store.getters['Teachers/get_search_list'];
    },
    search_substitutes() {
      return (this.search_teachers || []).filter(this.teacherAvailable);
    },
    is_old() {
      if (moment(this.plan_for_date) < moment().subtract(1, 'days')) return true;
      return false;
    },
  },

  methods: {
    substituteWarning(row) {
      const conflicts = substituteConflicts(row, this.teacherRecords, this.latest_plan,
        this.plan_for_date, this.$store.getters['Account/epochs'] || []);
      if (!conflicts.length) return '';
      return `Achtung: regulärer Unterricht – ${conflicts.map((lesson) => `${lesson.name}: ${lesson.class} (${lesson.subject})`).join('; ')}`;
    },
    standbyTeachers(row) {
      const date = this.latest_plan.daydate || this.plan_for_date;
      const dayIndex = (moment(date).day() + 6) % 7;
      const days = this.$store.getters['Account/standbys'] || [];
      const day = days[dayIndex];
      const hours = (row.hours && row.hours.length ? row.hours : [row.hour]).map(String);
      const configured = ((day && day.lessons) || [])
        .filter((lesson) => hours.includes(String(lesson.name)))
        .reduce((result, lesson) => result.concat(lesson.teachers || []), []);
      const entries = new Map();
      [...(row.alternatives || []), ...configured].forEach((teacher) => {
        const id = String(teacher.id);
        const current = this.teacherRecords.find((entry) => String(entry.id) === id);
        entries.set(id, { ...teacher, ...entries.get(id), ...current });
      });
      return Array.from(entries.values())
        .sort((a, b) => (a.standby_hours || 0) - (b.standby_hours || 0));
    },
    standbyHoursWeek(teacher) {
      const current = this.teacherRecords.find((entry) => String(entry.id)
        === String(teacher.id));
      const value = (current || teacher).standby_hours_week;
      return value === undefined || value === null ? '-' : value;
    },
    substituteLabel(row) {
      const teacher = row.substitute || {};
      const available = teacher.id && (row.free_teachers || [])
        .find((candidate) => String(candidate.id) === String(teacher.id));
      const classes = (available && available.absent_classes) || teacher.absent_classes || [];
      return `${teacher.lastname || ''}${classes.length ? ` (${classes.join(', ')})` : ''}${row.class_absence_substitution ? ' · abw.' : ''}`;
    },
    set_all_fixed(fixed) {
      this.$store.commit('Plans/set_all_fixed', fixed);
    },
    teacherAvailable(teacher) {
      return teacherAvailable(teacher, this.teacherRecords, this.latest_plan, this.plan_for_date);
    },
    filterTeachers(teachers) {
      const query = this.teacherQuery.trim().toLocaleLowerCase('de');
      return (teachers || []).filter((teacher) => {
        const name = `${teacher.firstname || ''} ${teacher.lastname || ''}`;
        return this.teacherAvailable(teacher) && name.toLocaleLowerCase('de').includes(query);
      });
    },
    async set_published(published) {
      this.publishing = true;
      try {
        const saved = await this.$store.dispatch('Plans/set_published', published);
        if (!saved) throw new Error('Publication could not be saved');
        this.$buefy.toast.open({
          message: published ? 'Vertretungsplan veröffentlicht.' : 'Veröffentlichung aufgehoben.',
          type: 'is-success',
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Veröffentlichung konnte nicht gespeichert werden.',
          type: 'is-danger',
        });
      } finally {
        this.publishing = false;
      }
    },
    rowcolor(row) {
      if (row.substitute.lastname === 'offen') {
        return 'alert';
      }
      if (row.combined === true
        || (row.combined === undefined && String(row.comment || '').includes('(ganze Klasse)'))) {
        return 'combined-class';
      }
      if (row.color === 'grey') {
        return 'agree_grey';
      }
      return '';
    },
    substchange(row, selection) {
      this.$store.commit('Plans/select_substitute', { row, selection });
      this.$store.commit('Plans/mutate_remove_double', row);
    },
    make_plan() {
      this.isLoading = true;
      this.$store.dispatch('Plans/make_plan').then(() => {
        this.get_plan();
        this.$buefy.toast.open({
          message: 'Plan aktualisiert.',
          type: 'is-success',
        });
        this.isLoading = false;
      });
    },
    close_plan_modal() {
      this.plan_modal = false;
    },
    openLoading() {
      this.isLoading = true;
    },
    show_reg_plan() {
      this.plan_modal = true;
    },
    toggle_send_modal() {
      this.send_modal = !this.send_modal;
    },
    async prepare_send_plan() {
      if (await this.save_plan()) this.send_modal = true;
    },
    go_next() {
      this.plan_for_date = moment(this.plan_for_date).add(1, 'days')['_d'];
      this.get_plan();
    },
    go_back() {
      this.plan_for_date = moment(this.plan_for_date).subtract(1, 'days')['_d'];
      this.get_plan();
    },
    get_plan() {
      return this.$store.dispatch('Plans/get_plan', this.plan_for_date);
    },
    async save_plan() {
      if (this.saving) return false;
      this.saving = true;
      try {
        const response = await this.$store.dispatch('Plans/save_plan');
        if (!response) throw new Error('Plan could not be saved');
        this.$buefy.toast.open({
          message: 'Vertretungsplan wurde gespeichert',
          type: 'is-success',
        });
        this.edit = false;
        return true;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Vertretungsplan konnte nicht gespeichert werden.',
          type: 'is-danger',
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    send_plan() {
      this.$store.dispatch('Plans/send_plan', { date: this.plan_for_date, text: this.email_text, recipient: this.recipient }).then((response) => {
        if (response) {
          this.$buefy.toast.open({
            message: 'Vertretungsplan wurde versendet',
            type: 'is-success',
          });
          this.send_modal = false;
        } else {
          this.$buefy.toast.open({
            message: 'Vertretungsplan konnte nicht versendet werden.',
            type: 'is-danger',
          });
        }
      });
    },
    add_sub() {
      this.edit_modal = true;
    },
    save_sub() {
      if (!this.entry.substitute || !this.entry.missing) {
        this.$buefy.toast.open({
          message: 'Bitte Fehlend und Vertretung eintragen.',
          type: 'is-danger',
        });
        return;
      }
      this.entry.type = 'm';
      this.entry.fix = true;
      this.$store.commit('Plans/save_sub', this.entry);
      this.edit_modal = false;
      this.name = '';
      this.name_sub = '';
      this.entry = {};
      this.save_plan();
    },
    reset_selected() {
      this.edit_modal = false;
    },
    search() {
      if (this.name === '') return;
      this.$store.dispatch('Teachers/search_teacher', this.name);
    },
    search_sub() {
      if (this.name === '') return;
      this.$store.dispatch('Teachers/search_teacher', this.name_sub);
    },
    select_missing(teacher) {
      if (!teacher) return;
      this.entry.missing = teacher;
      this.entry.missing.subjects = [];
    },
    select_sub(teacher) {
      if (!teacher) return;
      this.entry.substitute = teacher;
      this.entry.substitute.subjects = [];
    },
  },
};
</script>

<style lang="scss" local>

.dashboard {
  background-color: white;
  .table {
    tr.is-selected {
      background-color: var(--secondary-color)!important;
    }
    .tc{
      padding: 0pt;
    }
    tr {
      cursor: pointer;
    }
    .dropdown {
      width: 100% !important;
    }
  }
}
.substitute-dropdown {
  .dropdown-item {
    padding-top: .2rem;
    padding-bottom: .2rem;
    line-height: 1.25;
  }
  .dropdown-content hr {
    margin: .4rem 0;
  }
}
.textarea {
  min-height: 4rem !important;
}
.comment-input textarea.textarea {
  min-height: 0 !important;
  max-height: none;
  overflow-y: hidden;
  resize: none;
}
.plan-date-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}
.date-button {
  background-color: var(--secondary-color) !important;
  color: white !important;
  border-radius: 0 !important;
  width: 3.5rem;
  border: none;
  border-radius: 6px !important;
  cursor: pointer;
}
.alert {
  background-color: rgb(255, 94, 94) !important;
}
.table tr.combined-class,
.table tr.combined-class:hover {
  background-color: #d9efff !important;
}
.agree_grey {
  background-color: rgb(141, 141, 141) !important;
}
.inline-flex {
  display: inline-flex;
}

.mail-input {
  width: 100%;
  height: 11rem !important;
}
</style>

<style scoped>
.substitute-warning {
  display: inline-block;
  color: #946200;
  margin-left: 0.4rem;
  cursor: help;
}
</style>
